import QRCodeStyling from 'qr-code-styling'
import logoSquare from '@/assets/images/logo-square.png'

class QrCodeService {
  /**
   * Génère un QR Code stylisé avec le logo ZEborne au milieu
   * @param {string} link - L'URL à encoder dans le QR code
   * @param {number} width - Largeur du QR code en pixels
   * @returns {Promise<string>} Data URL du QR code (PNG base64)
   */
  async generateQrCode(link, width = 256) {
    if (!link) {
      return Promise.reject(new Error('Link is required'))
    }

    try {
      const qrCode = new QRCodeStyling({
        width: width,
        height: width,
        data: link,
        margin: 0,
        qrOptions: {
          typeNumber: 0,
          mode: 'Byte',
          errorCorrectionLevel: 'H' // High level pour supporter le logo
        },
        imageOptions: {
          hideBackgroundDots: true,
          imageSize: 0.6,
          margin: 10,
          crossOrigin: 'anonymous'
        },
        dotsOptions: {
          type: 'rounded', // Points arrondis pour un look moderne
          color: '#FF6B00', // Orange ZEborne
          gradient: {
            type: 'linear',
            rotation: 0,
            colorStops: [
              { offset: 0, color: '#FF6B00' },
              { offset: 1, color: '#FF8C00' }
          ]
          }
        },
        backgroundOptions: {
          color: '#FFFFFF'
        },
        cornersSquareOptions: {
          type: 'extra-rounded', // Coins extra arrondis
          color: '#FF6B00'
        },
        cornersDotOptions: {
          type: 'dot',
          color: '#FF6B00'
        },
        image: logoSquare
      })

      // Générer en tant que Data URL
      return new Promise((resolve, reject) => {
        qrCode.getRawData('png').then(blob => {
          const reader = new FileReader()
          reader.onloadend = () => {
            resolve(reader.result)
          }
          reader.onerror = reject
          reader.readAsDataURL(blob)
        }).catch(reject)
      })
    } catch (error) {
      console.error('Error generating QR code:', error)
      throw error
    }
  }

  /**
   * Télécharge un QR code en tant que fichier PNG
   * @param {string} dataUrl - Data URL du QR code
   * @param {string} filename - Nom du fichier à télécharger
   */
  downloadQrCode(dataUrl, filename = 'qrcode.png') {
    const link = document.createElement('a')
    link.href = dataUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  /**
   * Copie un lien dans le presse-papier
   * @param {string} link - Le lien à copier
   * @returns {Promise<void>}
   */
  copyToClipboard(link) {
    if (!link) {
      return Promise.reject(new Error('Link is required'))
    }

    // Utiliser l'API Clipboard si disponible
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(link)
    }

    // Fallback pour les anciens navigateurs
    return new Promise((resolve, reject) => {
      const textArea = document.createElement('textarea')
      textArea.value = link
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        document.execCommand('copy')
        textArea.remove()
        resolve()
      } catch (err) {
        textArea.remove()
        reject(err)
      }
    })
  }

  /**
   * Vérifie si un lien est valide
   * @param {string} link - Le lien à vérifier
   * @returns {boolean}
   */
  isValidLink(link) {
    if (!link || typeof link !== 'string') return false
    try {
      new URL(link)
      return true
    } catch {
      return false
    }
  }
}

export default new QrCodeService()
